// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// See ADDITIONAL-TERMS.md for the section 7(b) attribution requirement.
//
// Teeter — bootstrap and the screen machine. Owns which screen is up, the room's Net
// (created ONCE per session, never left for a rematch), the tilt input source, and
// the rAF game loop that feeds tilt → physics → render. Rules live in game.ts, the
// board in render.ts, the race in race.ts.

import '@ben-gy/game-engine/mobile.css';
import './styles/main.css';

import { hardenViewport } from '@ben-gy/game-engine/mobile';
import { createSfx } from '@ben-gy/game-engine/sound';
import { createStore } from '@ben-gy/game-engine/storage';
import { createNet, roomAppId, setTurnConfig, type Net } from '@ben-gy/game-engine/net';
import { getTurnConfig } from '@ben-gy/game-engine/turn';
import { createRounds, type RoundInfo, type Rounds, type RoundPlayer } from '@ben-gy/game-engine/rematch';
import { clearRoomInUrl, createLobby, createRoomEntry, normalizeRoomCode, setRoomInUrl } from '@ben-gy/game-engine/lobby';
import { resolveName } from '@ben-gy/game-engine/identity';

import { escapeHtml, fmtTime } from './dom';
import { MODE_IDS, modeOf, type Mode } from './modes';
import { createGame, drainEvents, step as stepGame, type GameEvent, type GameState } from './game';
import { dailySeed, todayUTC, newSeed, seedToCode } from './seed';
import { Board } from './render';
import { Race, seatOf, createOneShot, type RaceMsg } from './race';
import { SmartBot } from './bot';
import { runCountdown } from './countdown';
import {
  createGyroSource,
  createKeySource,
  createSyntheticSource,
  createTouchSource,
  gyroExists,
  requestGyroPermission,
  type SourceKind,
  type SyntheticSource,
  type TiltSource,
} from './input';
import { aboutHtml, ATTRIB, helpHtml, menuHtml, multiResultsHtml, soloResultsHtml } from './ui';

const SLUG = 'teeter';

// ── shell ─────────────────────────────────────────────────────────────────────
const app = document.querySelector<HTMLElement>('#app')!;
let content: HTMLElement;

const store = createStore(SLUG);
const sfx = createSfx(store.get<boolean>('muted', false) ?? false);
const reducedMotion = typeof matchMedia === 'function' && matchMedia('(prefers-reduced-motion: reduce)').matches;

let unlocked = false;
function unlock(): void {
  if (unlocked) return;
  unlocked = true;
  try {
    sfx.unlock();
  } catch {
    /* audio unlock is best-effort */
  }
}
window.addEventListener('pointerdown', unlock, { once: true });
window.addEventListener('keydown', unlock, { once: true });

// ── persistent session state ───────────────────────────────────────────────────
let playerName = resolveName(store, () => `Player ${Math.floor(Math.random() * 900 + 100)}`);
void playerName;
let myModeChoice = modeOf(store.get<string>('mode', '')).id;
let hostOptsMode = myModeChoice;

const bestKey = (id: string): string => `best_${id}`;
function bestFor(id: string): number {
  return store.get<number>(bestKey(id), 0) ?? 0;
}
function recordBest(id: string, ms: number): boolean {
  const cur = bestFor(id);
  if (cur === 0 || ms < cur) {
    store.set(bestKey(id), ms);
    return true;
  }
  return false;
}

let net: Net | null = null;
let rounds: Rounds | null = null;
let race: Race | null = null;
let roster: RoundPlayer[] = [];
let tally: number[] = [];
let roomCode = '';

// ── tilt input ─────────────────────────────────────────────────────────────────
let inputMode: SourceKind | null = null;
let inputForced: SourceKind | null = null;
let heldGyro: ReturnType<typeof createGyroSource> | null = null;
let heldKey: TiltSource | null = null;
let synthetic: SyntheticSource | null = null; // test hook only
const userPrefersTouch = (): boolean => store.get<boolean>('preferTouch', false) ?? false;

function isTouchDevice(): boolean {
  return (typeof matchMedia === 'function' && matchMedia('(pointer: coarse)').matches) || 'ontouchstart' in window;
}

/** Decide the input scheme — MUST be called from a user gesture (iOS permission). */
async function prepareInput(): Promise<void> {
  if (synthetic) {
    inputMode = 'key';
    return;
  }
  if (inputForced) {
    inputMode = inputForced;
    if (inputMode === 'key' && !heldKey) heldKey = createKeySource();
    return;
  }
  if (!isTouchDevice()) {
    inputMode = 'key';
    if (!heldKey) heldKey = createKeySource();
    return;
  }
  if (userPrefersTouch() || !gyroExists()) {
    inputMode = 'touch';
    return;
  }
  const perm = await requestGyroPermission();
  if (perm !== 'granted') {
    inputMode = 'touch';
    return;
  }
  const g = heldGyro ?? createGyroSource();
  const ok = await g.ready(800);
  if (ok) {
    heldGyro = g;
    inputMode = 'gyro';
  } else {
    if (!heldGyro) g.dispose();
    inputMode = 'touch';
  }
}

interface GameSource {
  source: TiltSource;
  announce: string;
  perGame: boolean;
}
function makeGameSource(boardHost: HTMLElement): GameSource {
  if (synthetic) return { source: synthetic, announce: 'Test feed', perGame: false };
  if (inputMode === 'gyro' && heldGyro) {
    heldGyro.recenter();
    return { source: heldGyro, announce: '', perGame: false };
  }
  if (inputMode === 'key') {
    if (!heldKey) heldKey = createKeySource();
    return { source: heldKey, announce: 'Use the arrow keys (or WASD) to tilt', perGame: false };
  }
  const src = createTouchSource(boardHost, undefined, reducedMotion);
  return {
    source: src,
    announce: gyroExists() ? 'No tilt sensor — drag anywhere to steer' : 'Drag anywhere to steer',
    perGame: true,
  };
}

// ── screen lifecycle ────────────────────────────────────────────────────────────
const cleanups: Array<() => void> = [];
function onCleanup(fn: () => void): void {
  cleanups.push(fn);
}
function teardown(): void {
  for (const fn of cleanups.splice(0)) {
    try {
      fn();
    } catch {
      /* a failing teardown must never block the next screen */
    }
  }
}
function setPlaying(on: boolean): void {
  document.body.classList.toggle('playing', on);
}

function mountShell(): void {
  app.innerHTML = `<main class="main-content" id="content"></main><footer class="site-footer">${ATTRIB}</footer>`;
  content = document.getElementById('content')!;
}

function sheet(html: string): void {
  const wrap = document.createElement('div');
  wrap.className = 'overlay';
  wrap.innerHTML = `<div class="sheet">${html}</div>`;
  document.body.appendChild(wrap);
  const close = (): void => wrap.remove();
  wrap.addEventListener('pointerdown', (e) => {
    if (e.target === wrap) close();
  });
  wrap.querySelectorAll('.sheet-close').forEach((b) => b.addEventListener('click', close));
}

function toast(msg: string): void {
  const t = document.createElement('div');
  t.className = 'toast';
  t.textContent = msg;
  document.body.appendChild(t);
  setTimeout(() => t.classList.add('show'), 10);
  setTimeout(() => t.remove(), 1900);
}

// ── menu ────────────────────────────────────────────────────────────────────────
function showMenu(): void {
  teardown();
  setPlaying(false);
  content.innerHTML = menuHtml({ modeId: myModeChoice, best: bestFor(myModeChoice), muted: sfx.muted(), dateStr: todayUTC() });

  content.querySelectorAll<HTMLElement>('.chip').forEach((c) =>
    c.addEventListener('click', () => {
      myModeChoice = modeOf(c.dataset.mode).id;
      store.set('mode', myModeChoice);
      try {
        sfx.play('select');
      } catch {
        /* best-effort */
      }
      showMenu();
    }),
  );
  content.querySelector('#playDaily')!.addEventListener('click', () => void startSolo(true));
  content.querySelector('#playRandom')!.addEventListener('click', () => void startSolo(false));
  content.querySelector('#playFriends')!.addEventListener('click', () => void beginFriends());
  content.querySelector('#help')!.addEventListener('click', () => sheet(helpHtml()));
  content.querySelector('#about')!.addEventListener('click', () => sheet(aboutHtml()));
  content.querySelector('#mute')!.addEventListener('click', () => {
    sfx.setMuted(!sfx.muted());
    store.set('muted', sfx.muted());
    showMenu();
  });

  if (!store.get<boolean>('seenHelp', false)) {
    store.set('seenHelp', true);
    sheet(helpHtml());
  }
}

// ── the game screen (shared by solo + multiplayer) ────────────────────────────────
interface GameConfig {
  mode: Mode;
  state: () => GameState;
  advance: (tilt: { x: number; y: number }, dtMs: number) => GameEvent[];
  isOver: () => boolean;
  onOver: () => void;
  onQuit: () => void;
  allowRestart: boolean;
  onRestart?: () => void;
  rivals?: () => Array<{ name: string; gemsLeft: number; falls: number; finished: boolean; won: boolean }>;
}
interface GameScreen {
  destroy(): void;
}

// The active tilt source of the current game screen, exposed only so the browser-
// verification hook can drive the REAL input→physics path synchronously (rAF is
// throttled in a hidden tab). Never read by gameplay.
let activeSource: TiltSource | null = null;

function juice(events: GameEvent[]): void {
  for (const e of events) {
    try {
      if (e.t === 'gem') sfx.play('coin', { pitch: 0.9 + Math.min(1, e.i / 5) * 0.6 });
      else if (e.t === 'fall') sfx.play('lose');
      else if (e.t === 'bump') sfx.play('blip', { pitch: 0.8 });
      else if (e.t === 'win') sfx.play('win');
      else if (e.t === 'respawn') sfx.play('select', { pitch: 0.6 });
    } catch {
      /* audio best-effort */
    }
    if (!reducedMotion && 'vibrate' in navigator) {
      try {
        if (e.t === 'gem') navigator.vibrate(10);
        else if (e.t === 'fall') navigator.vibrate([20, 40]);
        else if (e.t === 'win') navigator.vibrate([10, 20, 10]);
      } catch {
        /* haptics best-effort */
      }
    }
  }
}

function buildGameScreen(cfg: GameConfig): GameScreen {
  const mode = cfg.mode;
  content.innerHTML = `
    <section class="screen game">
      <div class="hud">
        <span class="hud-mode">${escapeHtml(mode.name)}</span>
        <span class="hud-timer" id="hudTimer">0:00.0</span>
        <span class="hud-extra" id="hudExtra"></span>
        <button class="iconbtn" id="recenter" title="Recenter" hidden>⌖</button>
        <button class="iconbtn" id="pause" title="Pause">❚❚</button>
        <button class="linkbtn hud-quit" id="quit">Quit</button>
      </div>
      <div class="timer-wrap"><span class="timer-bar" id="timerBar"></span></div>
      <div class="rivals" id="rivals" hidden></div>
      <div class="board-host" id="boardHost">
        <canvas id="cv"></canvas>
        <div class="announce" id="announce" hidden></div>
      </div>
    </section>`;

  const boardHost = content.querySelector<HTMLElement>('#boardHost')!;
  const canvas = content.querySelector<HTMLCanvasElement>('#cv')!;
  const hudTimer = content.querySelector<HTMLElement>('#hudTimer')!;
  const hudExtra = content.querySelector<HTMLElement>('#hudExtra')!;
  const timerBar = content.querySelector<HTMLElement>('#timerBar')!;
  const announce = content.querySelector<HTMLElement>('#announce')!;
  const recenterBtn = content.querySelector<HTMLButtonElement>('#recenter')!;
  const pauseBtn = content.querySelector<HTMLButtonElement>('#pause')!;
  const rivalsEl = content.querySelector<HTMLElement>('#rivals')!;
  content.querySelector('#quit')!.addEventListener('click', cfg.onQuit);

  const board = new Board(canvas);
  board.setMaze(cfg.state().maze);
  const doResize = (): void => {
    board.resize();
  };
  requestAnimationFrame(doResize);
  window.addEventListener('resize', doResize);
  const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(doResize) : null;
  ro?.observe(boardHost);

  const gs = makeGameSource(boardHost);
  activeSource = gs.source;
  if (gs.announce) {
    announce.textContent = gs.announce;
    announce.hidden = false;
    setTimeout(() => (announce.hidden = true), 2600);
  }
  if (inputMode === 'gyro' && !synthetic) {
    recenterBtn.hidden = false;
    recenterBtn.addEventListener('click', () => gs.source.recenter());
  }

  let live = false;
  let paused = false;
  let overHandled = false;
  let destroyed = false;
  let last = performance.now();
  let raf = 0;
  let hudTick = 0;

  const cd = runCountdown(boardHost, sfx, () => {
    if (destroyed) return;
    live = true;
    gs.source.recenter();
    last = performance.now();
  });

  function updateHud(state: GameState): void {
    hudTimer.textContent = fmtTime(state.elapsedMs);
    const remain = 1 - state.elapsedMs / mode.limitMs;
    timerBar.style.width = `${Math.max(0, Math.min(1, remain)) * 100}%`;
    timerBar.classList.toggle('low', remain <= 0.25);
    if (mode.gems > 0) hudExtra.textContent = `◆ ${mode.gems - state.gemsLeft}/${mode.gems}`;
    else if (mode.holes > 0) hudExtra.textContent = state.falls > 0 ? `Falls ${state.falls}` : '';
    else hudExtra.textContent = '';
    if (cfg.rivals) {
      const rs = cfg.rivals();
      if (rs.length) {
        rivalsEl.hidden = false;
        rivalsEl.innerHTML = rs
          .map((r) => {
            const prog = r.finished
              ? r.won
                ? '✓'
                : '—'
              : mode.gems > 0
                ? `${mode.gems - r.gemsLeft}/${mode.gems}`
                : mode.holes > 0
                  ? `${r.falls}✕`
                  : '…';
            return `<span class="rival${r.finished ? ' done' : ''}">${escapeHtml(r.name)} <b>${prog}</b></span>`;
          })
          .join('');
      }
    }
  }

  function loop(now: number): void {
    if (destroyed) return;
    const dt = Math.min(80, now - last);
    last = now;
    const state = cfg.state();
    if (live && !paused && !cfg.isOver()) {
      const events = cfg.advance(gs.source.tilt(), dt);
      board.onEvents(events, reducedMotion);
      juice(events);
    }
    board.frame(state, { dtMs: dt, reducedMotion });
    if (++hudTick % 3 === 0) updateHud(state);
    if (cfg.isOver() && !overHandled) {
      overHandled = true;
      updateHud(state);
      setTimeout(() => {
        if (!destroyed) cfg.onOver();
      }, 620);
    }
    raf = requestAnimationFrame(loop);
  }
  raf = requestAnimationFrame(loop);
  updateHud(cfg.state());

  function setPaused(p: boolean): void {
    if (destroyed || cfg.isOver()) return;
    paused = p;
    let ov = boardHost.querySelector<HTMLElement>('.pause-ov');
    if (p) {
      if (!ov) {
        ov = document.createElement('div');
        ov.className = 'pause-ov';
        ov.innerHTML = `<div class="pause-card"><h2>Paused</h2>
          <button class="btn primary" id="resume">Resume</button>
          ${cfg.allowRestart ? '<button class="btn" id="restart">Restart</button>' : ''}
          <button class="btn ghost" id="pquit">Quit</button></div>`;
        boardHost.appendChild(ov);
        ov.querySelector('#resume')!.addEventListener('click', () => setPaused(false));
        ov.querySelector('#restart')?.addEventListener('click', () => cfg.onRestart?.());
        ov.querySelector('#pquit')!.addEventListener('click', cfg.onQuit);
      }
    } else {
      ov?.remove();
      last = performance.now();
    }
  }
  pauseBtn.addEventListener('click', () => setPaused(!paused));

  function onKey(e: KeyboardEvent): void {
    if (e.key === 'p' || e.key === 'P') setPaused(!paused);
    else if ((e.key === 'r' || e.key === 'R') && cfg.allowRestart) cfg.onRestart?.();
    else if (e.key === ' ' && inputMode === 'gyro') gs.source.recenter();
  }
  window.addEventListener('keydown', onKey);

  return {
    destroy() {
      destroyed = true;
      if (activeSource === gs.source) activeSource = null;
      cancelAnimationFrame(raf);
      cd.cancel();
      window.removeEventListener('resize', doResize);
      window.removeEventListener('keydown', onKey);
      ro?.disconnect();
      if (gs.perGame) gs.source.dispose();
      board.destroy();
    },
  };
}

// ── solo ──────────────────────────────────────────────────────────────────────────
let soloState: GameState | null = null;
let soloDaily = false;
let soloScreen: GameScreen | null = null;

async function startSolo(daily: boolean): Promise<void> {
  await prepareInput();
  teardown();
  setPlaying(true);
  const mode = modeOf(myModeChoice);
  soloDaily = daily;
  const seed = daily ? dailySeed(mode.id, todayUTC()) : newSeed();
  soloState = createGame(mode, seed);

  soloScreen = buildGameScreen({
    mode,
    state: () => soloState!,
    advance: (tilt, dt) => {
      stepGame(soloState!, tilt, dt);
      return drainEvents(soloState!);
    },
    isOver: () => soloState!.over,
    onOver: () => endSolo(),
    onQuit: () => showMenu(),
    allowRestart: true,
    onRestart: () => void startSolo(soloDaily),
  });
  onCleanup(() => soloScreen?.destroy());
}

function endSolo(): void {
  const st = soloState!;
  const mode = st.mode;
  let isNewBest = false;
  if (st.finished) isNewBest = recordBest(mode.id, Math.round(st.elapsedMs));
  showSoloResults(isNewBest);
}

function showSoloResults(isNewBest: boolean): void {
  teardown();
  setPlaying(false);
  const st = soloState!;
  const mode = st.mode;
  content.innerHTML = `
    ${soloResultsHtml({
      won: st.finished,
      ms: Math.round(st.elapsedMs),
      falls: st.falls,
      gemsLeft: st.gemsLeft,
      mode,
      best: bestFor(mode.id),
      daily: soloDaily,
      isNewBest,
    })}
    <div class="menu-actions">
      <button class="btn primary" id="again">Play again</button>
      <button class="btn" id="share">Share ${st.finished ? 'time' : 'this maze'}</button>
      <button class="btn ghost" id="menu">Main menu</button>
      <button class="btn ghost" id="feedback">Report a problem</button>
    </div>`;
  content.querySelector('#again')!.addEventListener('click', () => void startSolo(soloDaily));
  content.querySelector('#menu')!.addEventListener('click', showMenu);
  content.querySelector('#feedback')!.addEventListener('click', (e) => window.feedback?.open({ returnFocusTo: e.currentTarget as HTMLElement }));
  content.querySelector('#share')!.addEventListener('click', () => void shareSolo(st, mode));
}

function shareSolo(st: GameState, mode: Mode): Promise<void> {
  const head = soloDaily ? `Teeter ${todayUTC()} · ${mode.name}` : `Teeter · ${mode.name}`;
  const line = st.finished ? `rolled it in ${fmtTime(Math.round(st.elapsedMs))}` : `couldn't beat this ${mode.name} maze`;
  const link = soloDaily ? 'https://teeter.benrichardson.dev' : `https://teeter.benrichardson.dev/?seed=${seedToCode(st.seed)}&m=${mode.id}`;
  return share(`${head} — ${line}\n${link}`);
}

async function share(text: string): Promise<void> {
  try {
    if (navigator.share) {
      await navigator.share({ text });
      return;
    }
  } catch {
    /* cancelled — fall through to copy */
  }
  try {
    await navigator.clipboard.writeText(text);
    toast('Copied to clipboard');
  } catch {
    toast('Could not copy');
  }
}

// ── multiplayer ───────────────────────────────────────────────────────────────────
let netSend: (m: RaceMsg) => void = () => {};
let stOff: () => void = () => {};
let lobby: { repaint(): void; destroy(): void } | null = null;
let lobbyMounted = false;
let gameScreen: GameScreen | null = null;

function repaintLobby(): void {
  if (lobbyMounted) lobby?.repaint();
}

async function beginFriends(): Promise<void> {
  await prepareInput(); // ask for the sensor now, while we still have the tap
  showRoomEntry();
}

function showRoomEntry(): void {
  teardown();
  setPlaying(false);
  content.innerHTML = '<section class="screen"><div id="entry"></div></section>';
  const host = content.querySelector<HTMLElement>('#entry')!;

  const deep = new URL(location.href).searchParams.get('room');
  if (deep) {
    const code = normalizeRoomCode(deep);
    clearRoomInUrl();
    if (code.length >= 3) {
      void enterRoom(code, false);
      return;
    }
  }

  const entry = createRoomEntry({
    container: host,
    onSubmit: (code: string, created: boolean) => void enterRoom(normalizeRoomCode(code), created),
    onCancel: showMenu,
    title: 'Play with friends',
    subtitle: 'Everyone races the same maze. Start a room, or type a friend’s code.',
  });
  onCleanup(() => entry.destroy());
}

async function enterRoom(code: string, created: boolean): Promise<void> {
  teardown();
  setPlaying(false);
  roomCode = code;
  setRoomInUrl(code);
  tally = [];
  race = null;

  content.innerHTML = `<section class="screen"><div class="lobby-searching"><span class="spinner"></span> Connecting…</div></section>`;

  try {
    setTurnConfig(await getTurnConfig());
  } catch {
    /* fail open — TURN is an upgrade, never a dependency */
  }

  const n = createNet(
    { appId: roomAppId(SLUG), roomId: code, claimHost: created },
    {
      onHostChange: (_id, isSelfHost) => {
        race?.setHost(isSelfHost);
        repaintLobby();
      },
      onPeers: () => repaintLobby(),
      onPeerLeave: (id) => {
        const seat = seatOf(roster, id);
        if (seat !== null) race?.peerLeft(seat);
        repaintLobby();
        refreshMultiResults();
      },
    },
  );
  net = n;

  const r = createRounds<{ mode: string }>({
    net: n,
    playerName,
    minPlayers: 2,
    roundOpts: () => ({ mode: myModeChoice }),
    onRound: (info) => startNetRound(info),
    onChange: (s) => {
      const ho = s.hostOpts as { mode?: string } | null;
      if (ho && typeof ho.mode === 'string') hostOptsMode = modeOf(ho.mode).id;
      repaintLobby();
    },
  });
  rounds = r;

  const sendStatus = n.channel<RaceMsg>('st', (data, from) => {
    if (!race) return;
    const seat = seatOf(roster, from);
    if (seat === null) return;
    race.receive(data, seat);
    refreshMultiResults();
  });
  netSend = (m) => sendStatus(m);
  stOff = () => sendStatus.off();

  showLobby();
}

function showLobby(): void {
  const n = net;
  const r = rounds;
  if (!n || !r) return;
  lobbyMounted = true;
  setPlaying(false);

  content.innerHTML = `<section class="screen"><div id="lobby-host"></div></section>`;
  const isHost = (): boolean => n.isHost() && n.hostSettled();

  lobby = createLobby({
    container: content.querySelector<HTMLElement>('#lobby-host')!,
    net: n,
    rounds: r,
    roomCode,
    minPlayers: 2,
    maxPlayers: 6,
    onCancel: () => void leaveRoom(),
    modeSlot: () => {
      if (isHost()) {
        return `<div class="lobby-modes" id="lm">${MODE_IDS.map(
          (id) =>
            `<button class="chip mode-btn${id === myModeChoice ? ' on' : ''}" data-mode="${id}" aria-pressed="${id === myModeChoice}">${escapeHtml(modeOf(id).name)}</button>`,
        ).join('')}<p class="mode-blurb">${escapeHtml(modeOf(myModeChoice).blurb)}</p></div>`;
      }
      return `<p class="host-pick">${n.hostSettled() ? `The host chose <strong>${escapeHtml(modeOf(hostOptsMode).name)}</strong>` : 'Waiting for the host…'}</p>`;
    },
    onModeMount: () => {
      if (!isHost()) return;
      content.querySelectorAll<HTMLElement>('#lm .mode-btn').forEach((b) =>
        b.addEventListener('click', () => {
          myModeChoice = modeOf(b.dataset.mode).id;
          store.set('mode', myModeChoice);
          lobby?.repaint();
        }),
      );
    },
  });
  onCleanup(() => {
    lobby?.destroy();
    lobby = null;
  });
}

function startNetRound(info: RoundInfo<{ mode: string }>): void {
  teardown();
  lobbyMounted = false;
  roster = info.players;
  if (tally.length !== roster.length) tally = Array(roster.length).fill(0);
  const mode = modeOf(info.opts?.mode);
  const selfSeat = info.seated ? seatOf(roster, net?.selfId ?? '') : null;
  const names = roster.map((p) => p.name);

  const rc = new Race({
    mode,
    seed: info.seed,
    selfSeat,
    names,
    isHost: info.isHost,
    deps: {
      send: (m) => netSend(m),
      onChange: () => refreshMultiResults(),
      onSelfEnd: () => {
        rounds?.finish();
        setTimeout(() => showMultiResults(), 700);
      },
    },
  });
  race = rc;
  setPlaying(true);

  if (selfSeat === null) {
    showMultiResults();
    return;
  }

  gameScreen = buildGameScreen({
    mode,
    state: () => rc.state,
    advance: (tilt, dt) => rc.step(tilt, dt),
    isOver: () => rc.state.over,
    onOver: () => {
      /* onSelfEnd already scheduled the results */
    },
    onQuit: () => backToLobby(),
    allowRestart: false,
    rivals: () =>
      [...rc.results.values()]
        .filter((p) => p.seat !== rc.selfSeat && !p.left)
        .map((p) => ({ name: p.name, gemsLeft: p.gemsLeft, falls: p.falls, finished: p.finished, won: p.won })),
  });
  onCleanup(() => {
    gameScreen?.destroy();
    gameScreen = null;
  });
}

// ── multiplayer results ──────────────────────────────────────────────────────────
let resultsMounted = false;
let winsAwarded = false;
const closeWait = createOneShot(25_000, () => refreshMultiResults());

function showMultiResults(): void {
  teardown();
  setPlaying(false);
  resultsMounted = true;
  winsAwarded = false;
  content.innerHTML = `
    <section class="screen results" id="mres"></section>
    <div class="menu-actions" id="mact">
      <button class="btn primary" id="again">Play again</button>
      <p class="waiting-note" id="waiting" aria-live="polite"></p>
      <button class="btn" id="lobby">Back to lobby</button>
      <button class="btn ghost" id="menu">Main menu</button>
      <button class="btn ghost" id="feedback">Report a problem</button>
    </div>`;
  const again = content.querySelector<HTMLButtonElement>('#again')!;
  again.addEventListener('click', () => {
    rounds?.vote();
    again.disabled = true;
    again.textContent = 'Waiting for the others…';
  });
  content.querySelector('#lobby')!.addEventListener('click', () => backToLobby());
  content.querySelector('#menu')!.addEventListener('click', () => void leaveRoom());
  content.querySelector('#feedback')!.addEventListener('click', (e) => window.feedback?.open({ returnFocusTo: e.currentTarget as HTMLElement }));
  onCleanup(() => {
    resultsMounted = false;
    closeWait.cancel();
  });
  closeWait.poke();
  refreshMultiResults();
}

function refreshMultiResults(): void {
  if (!resultsMounted || !race) return;
  const rc = race;
  const box = content.querySelector<HTMLElement>('#mres');
  if (!box) return;

  if (!winsAwarded && rc.everyoneDone()) {
    winsAwarded = true;
    const winners = rc.ranked().filter((r) => !r.left && r.won);
    if (winners.length) tally[winners[0].seat] = (tally[winners[0].seat] ?? 0) + 1; // fastest finisher
  }

  box.innerHTML = multiResultsHtml(rc.ranked(), rc.selfSeat, rc.mode, tally);

  const waiting = content.querySelector<HTMLElement>('#waiting');
  if (waiting) {
    const r = rounds?.state();
    if (r && r.startsInMs !== null) {
      waiting.textContent = `Next round in ${Math.ceil(r.startsInMs / 1000)}s — ${r.votes.length}/${Math.max(r.present.length, 1)} ready`;
    } else if (!rc.everyoneDone()) {
      const left = [...rc.results.values()].filter((x) => !x.finished && !x.left).map((x) => x.name);
      waiting.textContent = left.length ? `Still rolling: ${left.join(', ')}` : '';
    } else if (r && r.votes.length) {
      waiting.textContent = `${r.votes.length}/${Math.max(r.present.length, 1)} ready for another round`;
    } else {
      waiting.textContent = '';
    }
  }
}

function backToLobby(): void {
  teardown();
  race = null;
  if (net && rounds) showLobby();
  else showMenu();
}

async function leaveRoom(): Promise<void> {
  teardown();
  lobbyMounted = false;
  race = null;
  const n = net;
  const r = rounds;
  net = null;
  rounds = null;
  netSend = () => {};
  try {
    stOff();
  } catch {
    /* channel may already be gone */
  }
  stOff = () => {};
  r?.destroy();
  if (n) {
    try {
      await n.leave();
    } catch {
      /* leaving is best-effort */
    }
  }
  showMenu();
}

// ── boot ────────────────────────────────────────────────────────────────────────
function boot(): void {
  hardenViewport();
  mountShell();
  // An async-seed / daily deep link: ?seed=CODE&m=mode plays that exact board solo.
  const params = new URL(location.href).searchParams;
  const room = params.get('room');
  const seedParam = params.get('seed');
  if (room && normalizeRoomCode(room).length >= 3) {
    void beginFriends();
  } else if (seedParam) {
    const m = params.get('m');
    if (m) myModeChoice = modeOf(m).id;
    const seedNum = parseInt(seedParam.trim().toLowerCase(), 36);
    if (Number.isFinite(seedNum) && seedNum > 0) {
      clearRoomInUrl();
      void startSeed(seedNum >>> 0);
      return;
    }
    showMenu();
  } else {
    showMenu();
  }
  window.addEventListener('beforeunload', () => {
    void net?.leave();
  });
}

async function startSeed(seed: number): Promise<void> {
  await prepareInput();
  teardown();
  setPlaying(true);
  const mode = modeOf(myModeChoice);
  soloDaily = false;
  soloState = createGame(mode, seed);
  soloScreen = buildGameScreen({
    mode,
    state: () => soloState!,
    advance: (tilt, dt) => {
      stepGame(soloState!, tilt, dt);
      return drainEvents(soloState!);
    },
    isOver: () => soloState!.over,
    onOver: () => endSolo(),
    onQuit: () => showMenu(),
    allowRestart: true,
    onRestart: () => void startSolo(false),
  });
  onCleanup(() => soloScreen?.destroy());
}

// The browser-verification pass drives this to prove the tilt → physics → win wiring
// end-to-end WITHOUT a real sensor (synthetic feed), and to read state (test-only).
(window as unknown as { __teeter?: unknown }).__teeter = {
  useSynthetic(): void {
    synthetic = createSyntheticSource();
  },
  forceInput(kind: SourceKind): void {
    inputForced = kind;
  },
  tilt(x: number, y: number): void {
    synthetic?.set(x, y);
  },
  state(): { x: number; y: number; gemsLeft: number; falls: number; over: boolean; finished: boolean; ms: number } | null {
    if (!soloState && !race) return null;
    const s = soloState ?? race!.state;
    return { x: s.ball.x, y: s.ball.y, gemsLeft: s.gemsLeft, falls: s.falls, over: s.over, finished: s.finished, ms: s.elapsedMs };
  },
  mode(): string {
    return (soloState ?? race?.state)?.mode.id ?? '';
  },
  // Advance the CURRENT solo game synchronously (rAF is throttled in a hidden tab).
  // Reads the REAL active input source each chunk — so setting a synthetic tilt and
  // stepping proves the injected-sample → physics wiring; `bot:true` drives the maze
  // to a win, proving the objective is completable through the shipped code path.
  stepFor(ms: number, bot = false): unknown {
    const s = soloState ?? race?.state;
    if (!s) return null;
    const botCtrl = bot ? new SmartBot(s.maze) : null;
    const chunk = 1000 / 60;
    for (let t = 0; t < ms && !s.over; t += chunk) {
      const tilt = botCtrl ? botCtrl.tilt(s) : (activeSource?.tilt() ?? { x: 0, y: 0 });
      // In a live round, drive through Race.step so status is gossiped; else solo.
      if (soloState) stepGame(soloState, tilt, chunk);
      else race?.step(tilt, chunk);
    }
    return { x: s.ball.x, y: s.ball.y, gemsLeft: s.gemsLeft, falls: s.falls, over: s.over, finished: s.finished, ms: s.elapsedMs };
  },
};

boot();
